<template>
  <div ref="container" class="ImageComponent" :class="[containerClass]">
    <img
      ref="image"
      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      :alt="alt"
      :class="[imageClass]"
    />
    <div v-if="showSpinner" class="ImageComponent__spinner">
      <RotationLoader color="white" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { RotationLoader } from '@snowshoe-frontend/shared-components'

const props = defineProps({
  imgUrl: {
    type: String,
    default: '',
    required: true
  },
  alt: {
    type: String,
    default: ''
  },
  containerClass: {
    type: String,
    default: ''
  },
  imageClass: {
    type: String,
    default: ''
  },
  directlyRendering: {
    type: Boolean,
    default: false
  }
})

const container = ref(null)
const image = ref(null)
const showSpinner = ref(false)

// TODO THOMAS: REFACTOR CODESTYLE

// TODO THOMAS: Refactor to figure

// TODO THOMAS: Refactor intersection observer handling here
// window.imageWeakmap and window.ImageComponentObserver could be stored in a client side state store
// and maybe events trigger further handling of images

// TODO THOMAS: Flag for directly rendering an image without intersection observer
// TODO THOMAS: CONSIDER DPR AND CONNECTION QUALITY FOR IMAGE SIZE CALCULATION
// TODO THOMAS: MAX SIZE FOR IMAGE SIZE CALCULATION

function createObserver() {
  window.ImageComponentObserver = new IntersectionObserver(
    (entries) => {
      const items = []

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          items.push({
            target: entry.target,
            ratio: entry.intersectionRatio,
            top: entry.boundingClientRect.top,
            area:
              entry.boundingClientRect.width * entry.boundingClientRect.height
          })
        }
      })

      items.sort(function (a, b) {
        return b.ratio - a.ratio || a.top - b.top || b.area - a.area
      })

      items.forEach(function (item) {
        const data = window.imageWeakmap.get(item.target)
        if (data && data.image.value) {
          const loadingTimeout = setTimeout(() => {
            window.requestAnimationFrame(() => {
              data.showSpinner.value = true
            })
          }, 800)

          window.requestAnimationFrame(() => {
            const boundingClientRect = item.target.getBoundingClientRect()
            data.image.value.setAttribute('decoding', 'async')
            data.image.value.src = data.props.imgUrl
              .replace('%W%', Math.round(boundingClientRect.width))
              .replace('%H%', Math.round(boundingClientRect.height))
          })
          data.image.value.onload = () => {
            clearTimeout(loadingTimeout)

            window.requestAnimationFrame(() => {
              data.showSpinner.value = false
            })
          }

          window.ImageComponentObserver.unobserve(item.target)
        }
      })
    },
    {
      threshold: 0.8
    }
  )
}

onMounted(() => {
  if (!window.imageWeakmap) {
    window.imageWeakmap = new WeakMap()
  }

  window.imageWeakmap.set(container.value, {
    props,
    image,
    showSpinner
  })

  if (!props.directlyRendering && !window.ImageComponentObserver) {
    createObserver()
  }

  if (props.directlyRendering) {
    const boundingClientRect = container.value.getBoundingClientRect()
    image.value.src = props.imgUrl
      .replace('%W%', Math.round(boundingClientRect.width))
      .replace('%H%', Math.round(boundingClientRect.height))
  } else {
    window.ImageComponentObserver.observe(container.value)
  }
})

onBeforeUnmount(() => {
  if (window.ImageComponentObserver) {
    window.ImageComponentObserver.unobserve(container.value)
  }
  if (window.imageWeakmap.has(container.value)) {
    window.imageWeakmap.delete(container.value)
  }
})
</script>
<style lang="scss">
.ImageComponent {
  position: relative;
  width: 100%;
  height: 100%;

  > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
  }

  .ImageComponent__spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: +1;
  }
}
</style>
